// Domain · RecyclingProcessChip
//
// Code-coded pill for waste-treatment process codes (BDO/EU framework):
//   - R1..R13 — recovery (recykling) — green family
//   - D1..D15 — disposal (unieszkodliwianie) — red family
//   - anything else — grey fallback (raw text rendered, no crash)
//
// Codes are short alphanumerics (e.g. "R3", "D5") and appear on
// `edpr_records.recycling_process_code`, `transactions.recycling_process_code`,
// `edpr_rates.recycling_process_code`. Centralising the tone + label here
// removes the per-file `case "R3": ... return "purple"` ladders.

import React from "react";
import { Tag } from "@ds/display/Tag";
import type { PillSize } from "@ds/display/Pill";
import { TextValue } from "@ds/display/TextValue";
import { recyclingProcessTone } from "@ds/domain/RecyclingProcessChip.helpers";

export type { RecyclingProcessCategory } from "@ds/domain/RecyclingProcessChip.helpers";


// ─── Inputs ───────────────────────────────────────────────────────────────

export interface RecyclingProcessChipInput {
  /** Process code from the DB — `"R3"`, `"D5"`, …. `null` / empty → em-dash. */
  code: string | null | undefined;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Chip size — only meaningful when `variant="chip"`. Default `"sm"`. */
  size?: PillSize;
  /** `data-testid` on the root. Defaults to `"recycling-process-chip"`. */
  testId?: string;
}

export type RecyclingProcessChipProps = RecyclingProcessChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function RecyclingProcessChip({
  code,
  variant = "chip",
  size = "sm",
  testId = "recycling-process-chip",
}: RecyclingProcessChipProps) {
  if (!code) {
    return <span data-testid={testId}>—</span>;
  }

  const tone = recyclingProcessTone(code);
  const label = code.trim().toUpperCase();

  if (variant === "inline") {
    return (
      <TextValue tone={tone} tabular testId={testId}>
        {label}
      </TextValue>
    );
  }

  return <Tag label={label} tone={tone} size={size} testId={testId} />;
}
